import { useQueryClient } from "@tanstack/react-query";
import { notificacion, notificacionError } from "../../../../components/Alerts";
import { BaseModal, ModalCloseCross, type BaseModalProps } from "../../../../components/modals/BaseModal";
import { ComunicacionAjax } from "../../../../ts/api/comunicacion";
import type { Respuesta } from "../../../../ts/typings/clases";
import { keysColumn, UseEstanteria } from "../GestionUbicaciones";
import { useCallback, useEffect, useMemo, useState } from "react";

interface ModalEditarFilaProps extends BaseModalProps {
	readonly currentZona: string;
	readonly currentEstanteria: string;
}
export function ModalAddUbicaciones(props: ModalEditarFilaProps) {
	const { close } = props;
	const queryClient = useQueryClient();
	const [config, setConfig] = useState({
		zona: props.currentZona,
		estanteria: props.currentEstanteria,
		columnas: "A-A",
		alturas: "01-01"
	});
	const [excluded, setExcluded] = useState<string[]>([]);

	const ubicacionesExistentes = UseEstanteria(config.zona, config.estanteria);

	useEffect(() => {
		let minCol: string | null = null;
		let maxCol: string | null = null;
		let minAlt: number | null = null;
		let maxAlt: number | null = null;
		for (const ubicacion of ubicacionesExistentes.data ?? []) {
			if (minCol == null || ubicacion.idColumna < minCol) minCol = ubicacion.idColumna;
			if (maxCol == null || ubicacion.idColumna > maxCol) maxCol = ubicacion.idColumna;
			const altNum = Number(ubicacion.idAltura);
			if (minAlt == null || altNum < minAlt) minAlt = altNum;
			if (maxAlt == null || altNum > maxAlt) maxAlt = altNum;
		}
		minCol = minCol ?? "A";
		maxCol = maxCol ?? "A";
		minAlt = minAlt ?? 1;
		maxAlt = maxAlt ?? 1;

		setConfig(old => ({
			...old,
			columnas: minCol === "L" ? "A-A" : `${minCol}-${maxCol}`,
			alturas: minAlt === 99 ? "01-01" : `${minAlt}-${maxAlt}`,
		}));

		const defaultExcluded = new Array<string>();
		const minColIndex = keysColumn.indexOf(minCol);
		const maxColIndex = keysColumn.indexOf(maxCol);
		if (minColIndex === -1 || maxColIndex === -1 || minColIndex > maxColIndex) return;


		for (let colIndex = minColIndex; colIndex <= maxColIndex; colIndex++) {
			for (let rowIndex = minAlt; rowIndex <= maxAlt; rowIndex++) {
				const idUbicacion = `${config.zona}${config.estanteria}${keysColumn[colIndex]}${String(rowIndex).padStart(2, "0")}`;

				if (!ubicacionesExistentes.data?.some(e => e.idUbicacion === idUbicacion)) {
					defaultExcluded.push(idUbicacion);
				}
			}
		}


		setExcluded(defaultExcluded);
	}, [ubicacionesExistentes.data, config.zona, config.estanteria]);

	useEffect(() => {
		setConfig(old => ({
			...old,
			zona: props.currentZona,
			estanteria: props.currentEstanteria,
		}));
	}, [props.currentEstanteria, props.currentZona]);

	const ubicacionesCalientes = useMemo(() => {
		if (ubicacionesExistentes.data == null) return null;
		return getUbicacionesVisualizar(config, excluded, ubicacionesExistentes.data);
	}, [config, excluded, ubicacionesExistentes.data]);

	const guardarUbicacion = useCallback((e: React.FormEvent) => {
		e.preventDefault();
		if (ubicacionesCalientes == null) throw new Error("No hay ubicaciones para guardar.");

		const ubicaciones = ubicacionesCalientes.filter(u => u.nueva && !u.excluded).map(u => ({
			idUbicacion: u.idUbicacion,
			idAlmacen: u.idAlmacen,
			idEstanteria: u.idEstanteria,
			idColumna: u.idColumna,
			idAltura: u.idAltura,
		}));

		void ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", "/api/v1/insertUbicaciones", { ubicaciones })
			.then(response => {
				if (response.tipo == "error") {
					notificacionError(response.message ?? "Error al guardar la ubicación.");
					return;
				}

				notificacion(response.message ?? "Ubicación guardada con éxito.");
				close();
				void queryClient.invalidateQueries({ queryKey: ["getEstanterias"] });
				void queryClient.invalidateQueries({ queryKey: ["getEstanteria", config.zona, config.estanteria] });
			});
	}, [close, queryClient, config, ubicacionesCalientes]);

	return (
		<BaseModal show={props.show} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">Ubicación</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<form onSubmit={guardarUbicacion}>
				<div className="modal-body">
					<div style={{ display: "flex", justifyContent: "space-between", gap: "15px" }}>
						<FormNuevaUbicacion config={config} setConfig={setConfig} />
						{ubicacionesCalientes != null && <Estanteria zona={config.zona} fila={config.estanteria} ubicaciones={ubicacionesCalientes} setExcluded={setExcluded} />}
						{ubicacionesCalientes == null && <p>Configuracion incorrecta. Las filas deben de estar en el rango 01-99 y las columnas en el rango A-L.</p>}
					</div>
				</div>
				<div className="modal-footer">
					<button type="submit" className="btnPrimary">Guardar</button>
					<button type="button" className="btnSecondary" onClick={props.close}>Cerrar</button>
				</div>
			</form>
		</BaseModal>
	);
}

interface ConfigUbicaciones {
	zona: string;
	estanteria: string;
	columnas: string;
	alturas: string;
}
function FormNuevaUbicacion(props: { readonly config: ConfigUbicaciones; readonly setConfig: Setter<ConfigUbicaciones>; }) {
	const { config, setConfig } = props;


	return (
		<div id="divIdUbicacion" >
			<div>
				<label className="labelsForm">Zona</label>
				<select className="inputsForm" name="zona" value={config.zona} onChange={e => setConfig((config) => ({ ...config, zona: e.target.value }))}>
					<option value="A">A</option>
					<option value="B">B</option>
					<option value="C">C</option>
					<option value="D">D</option>
					<option value="E">E</option>
					<option value="F">F</option>
					<option value="G">G</option>
					<option value="H">H</option>
					<option value="I">I</option>
					<option value="J">J</option>
					<option value="K">K</option>
					<option value="L">L</option>
				</select>
			</div>
			<div>
				<label className="labelsForm">Fila</label>
				<input type="number" className="inputsForm" name="fila" value={config.estanteria} onChange={e => setConfig((config) => ({ ...config, estanteria: e.target.value }))} min={1} />
			</div>
			<div>
				<label className="labelsForm">Columna</label>
				<input type="text" className="inputsForm" name="columna" placeholder="A-#" value={config.columnas} onChange={e => setConfig((config) => ({ ...config, columnas: e.target.value }))} />
			</div>
			<div>
				<label className="labelsForm">Altura</label>
				<input type="text" className="inputsForm" name="altura" placeholder="1-#" value={config.alturas} onChange={e => setConfig((config) => ({ ...config, alturas: e.target.value }))} />
			</div>
		</div>
	);
}

function Estanteria(props: { readonly zona: string, readonly fila: string; readonly ubicaciones: BasicCeldaInfo[]; readonly setExcluded: Setter<string[]>; }) {
	const onClickUbi = (idUbi: BasicCeldaInfo) => {
		if (!idUbi.nueva) return;

		props.setExcluded(old => {
			if (old.includes(idUbi.idUbicacion)) {
				return old.filter(x => x !== idUbi.idUbicacion);
			} else {
				return [...old, idUbi.idUbicacion];
			}
		});
	};

	return (
		<div style={{ padding: "20px", flex: 1, border: "1px solid #ccc" }}>
			<h1 style={{ textAlign: "center", marginTop: 0 }}>{props.zona}{props.fila}</h1>
			<div className="tablaEstanteria">
				{props.ubicaciones.map(ubicacion => (
					<CeldaEstanteria key={ubicacion.idUbicacion} ubicacion={ubicacion} onClick={() => onClickUbi(ubicacion)} />
				))}
			</div>
		</div>
	);
}
function CeldaEstanteria(props: { readonly ubicacion: BasicCeldaInfo, readonly onClick?: () => void; }) {
	let claseFondo = "";
	if (props.ubicacion.nueva == true)
		claseFondo = props.ubicacion.excluded ? "fondoGone hoverBright" : "fondoVerde hoverDark";

	const idColumna = keysColumn.indexOf(props.ubicacion.idColumna) + 1;
	const idAltura = parseInt(props.ubicacion.idAltura) * -1;
	const style = {
		gridColumnStart: String(idColumna),
		gridColumnEnd: String(idColumna),
		gridRowStart: String(idAltura),
		gridRowEnd: String(idAltura),
		cursor: props.ubicacion.nueva ? "pointer" : "default"
	};
	return (
		<div className={`celdaUbicacion ${claseFondo}`} style={style} onClick={props.onClick}>
			<h3>{props.ubicacion.idUbicacion}</h3>
		</div>
	);
}


function getUbicacionesVisualizar(config: ConfigUbicaciones, excluded: string[], ubicacionesExistentes: BasicCeldaInfo[]): BasicCeldaInfo[] | null {
	const { zona, estanteria, columnas, alturas } = config;
	const lista: BasicCeldaInfo[] = structuredClone(ubicacionesExistentes);

	const columnasMatch = /^([A-L])-([A-L])$/.exec(columnas);
	if (!columnasMatch) return null;

	const startColIndex = keysColumn.indexOf(columnasMatch[1] as "A");
	const endColIndex = keysColumn.indexOf(columnasMatch[2] as "A");
	if (startColIndex === -1 || endColIndex === -1 || startColIndex > endColIndex) return null;

	const alturasMatch = /^(\d+)-(\d+)$/.exec(alturas);
	if (!alturasMatch) return lista;
	const startRowIndex = Number(alturasMatch[1]);
	const endRowIndex = Number(alturasMatch[2]);
	if (startRowIndex === -1 || endRowIndex === -1 || startRowIndex > endRowIndex) return null;

	for (let colIndex = startColIndex; colIndex <= endColIndex; colIndex++) {
		for (let rowIndex = startRowIndex; rowIndex <= endRowIndex; rowIndex++) {
			const idUbicacion = `${zona}${estanteria}${keysColumn[colIndex]}${String(rowIndex).padStart(2, "0")}`;

			if (lista.some(e => e.idUbicacion === idUbicacion)) continue;

			lista.push({
				idUbicacion: idUbicacion,

				idAlmacen: zona,
				idEstanteria: estanteria,
				idColumna: keysColumn[colIndex],
				idAltura: String(rowIndex).padStart(2, "0"),

				nueva: true,
				excluded: excluded.includes(idUbicacion),
			});
		}
	}

	return lista;
}

// async function insertUbicacion(zona: string, fila: number, columna: string, altura: number) {
// }
// function onClickNuevaUbi() {
// 	function getTablaFromUbicaciones(fila: UbicacionJJ[]) {
// 		function getTCellFromUbi(ubicacion: UbicacionJJ | null) {
// 			const td = $("<td>");
// 			td.addClass("celdaUbicacion");
// 			if (ubicacion == null) {
// 				td.css("background", "grey");
// 			} else {
// 				td.text(`${ubicacion.idAlmacen}${ubicacion.idFila}${keysColumn[ubicacion.idColumna]}${ubicacion.idAltura}`);
// 			}

// 			return td;
// 		}

// 		if (fila.length == 0) return $("<div class=\"border fillAll\">Sin ubicaciones</div>");
// 		let maxAltura = 0;
// 		let maxColumn = 0;

// 		const ubiPorAltura: Record<number, Record<number, UbicacionJJ>> = {};
// 		for (const ubicacion of fila) {
// 			if (ubiPorAltura[ubicacion.idAltura] == undefined)
// 				ubiPorAltura[ubicacion.idAltura] = [];

// 			ubiPorAltura[ubicacion.idAltura][ubicacion.idColumna] = ubicacion;
// 			maxAltura = Math.max(maxAltura, ubicacion.idAltura);
// 			maxColumn = Math.max(maxColumn, ubicacion.idColumna);
// 		}


// 		const tabla = $("<table class='tablaPreview'>");
// 		for (let idAlt = 0; idAlt <= maxAltura; idAlt++) {
// 			const ubicaciones = ubiPorAltura[idAlt] ?? [];
// 			const tr = $("<tr>");

// 			for (let idCol = 0; idCol <= maxColumn; idCol++) {
// 				const ubicacion = ubicaciones[idCol] ?? null;
// 				tr.append(getTCellFromUbi(ubicacion));
// 			}
// 			tabla.prepend(tr);
// 		}
// 		return tabla;
// 	}

// 	limpiarModal();
// 	const modal = $("#modalEditarFila");
// 	modal.find("[data-toggle=\"tooltip\"]").tooltip();
// 	const tabla = getTablaFromUbicaciones([]);
// 	modal.find("#divTablaEjemplo").empty().append(tabla);

// 	const estanteriaActiva = $(".active>.tabVertical>.tabLinks.active");
// 	modal.find("#comboAlmacen").val(estanteriaActiva.attr("idAlmacen")!);


// 	modal.modal({});
// }

export type BasicCeldaInfo = {
	idUbicacion: string;

	idAlmacen: string;
	idEstanteria: string;
	idColumna: typeof keysColumn[number];
	idAltura: string;

	articulos: Array<{
		cantidad: number;
		esFijo: boolean;
		idArticulo: string;
		idFase: number;
	}>;

	nueva?: false;
} | {
	idUbicacion: string;

	idAlmacen: string;
	idEstanteria: string;
	idColumna: typeof keysColumn[number];
	idAltura: string;

	nueva: true;
	excluded: boolean;
};
